import environment from '@lib/environment';
import IAppLogger, {
  LogLevel,
  LoggerExceptionFn,
  LoggerFn,
} from './IAppLogger';
import IAppManager from './IAppManager';

export const APP_LOGS_LEVEL_KEY = 'app.logs.level';
export const APP_LOGS_KEYS_KEY = 'app.logs.keys';

class AppLogs {
  private _loggers: Record<string, IAppLogger> = {};
  private _maxLogLevel: number;
  private _levels: Record<string, LogLevel> = {};
  private _originalConsoleDebug: any;
  private _originalConsoleLog: any;
  private _originalConsoleWarn: any;
  private _originalConsoleError: any;
  private _appManager: IAppManager;
  private _logger: IAppLogger;

  constructor(appManager: IAppManager) {
    this._appManager = appManager;
    this._logger = this.get('app');

    // set max log level
    this._maxLogLevel = this.logLevelToNumber(this.getDefaultLevelForEnv());

    // overide console
    this._originalConsoleDebug = console.debug;
    this._originalConsoleLog = console.log;
    this._originalConsoleWarn = console.warn;
    this._originalConsoleError = console.error;

    // for now we trap all logs to 'Unknown' logger
    // but still let the original console log
    const unknownLogger = this.get('Unknown', 'error');

    console.log = console.info = (...args: any[]) => {
      this._originalConsoleLog(...args);
      unknownLogger.info(JSON.stringify(args));
    };
    console.debug = (...args: any[]) => {
      this._originalConsoleDebug(...args);
      unknownLogger.debug(JSON.stringify(args));
    };
    console.warn = (...args: any[]) => {
      this._originalConsoleWarn(...args);
      unknownLogger.warn(JSON.stringify(args));
    };
    console.error = (...args: any[]) => {
      this._originalConsoleError(...args);
      unknownLogger.error(JSON.stringify(args));
    };

    // manage key levels in localstorage
    this.setupKeyStorage();
  }

  public get(key: string, level?: LogLevel): IAppLogger {
    if (level === undefined) {
      level = this.getDefaultLevelForEnv();
    }
    key = key.toLowerCase();
    if (!this._levels[key]) {
      this._levels[key] = level;
    }
    if (!this._loggers[key]) {
      this._loggers[key] = {
        debug: this.createLoggerFn(key, 'debug'),
        info: this.createLoggerFn(key, 'info'),
        warn: this.createLoggerFn(key, 'warn'),
        error: this.createLoggerFn(key, 'error'),
        exception: this.createLoggerExceptionFn(key),
      };
    }
    return this._loggers[key];
  }

  private createLoggerFn(key: string, level: LogLevel): LoggerFn {
    return (message: any, ...optionalParams: any[]) => {
      if (this.shouldLog(key, level)) {
        let logFn = this._originalConsoleLog;
        if (level === 'debug') {
          logFn = this._originalConsoleDebug;
        } else if (level === 'warn') {
          logFn = this._originalConsoleWarn;
        } else if (level === 'error') {
          logFn = this._originalConsoleError;

          // emit error event
          this._appManager.events.emit('error', {
            key: key.toLowerCase(),
            message,
            args: optionalParams[0],
          });
        }
        logFn(`${level}(${key.toLowerCase()}): ${message}`, optionalParams);
      }
    };
  }

  private createLoggerExceptionFn(key: string): LoggerExceptionFn {
    return (err: unknown, message: any, ...optionalParams: any[]) => {
      if (this.shouldLog(key, 'error')) {
        this._originalConsoleError(
          `exception(${key.toLowerCase()}): ${message}`,
          optionalParams,
          err,
        );
      }
    };
  }

  private logLevelToNumber(level: LogLevel | undefined): number {
    switch (level) {
      case 'debug':
        return 1;
      case 'info':
        return 2;
      case 'warn':
        return 3;
      case 'error':
        return 4;
      default:
        return 5;
    }
  }

  private shouldLog(key: string, level: LogLevel): boolean {
    const logKeyLevel = this.logLevelToNumber(this._levels[key.toLowerCase()]);
    const logLevel = this.logLevelToNumber(level);
    return logLevel >= this._maxLogLevel && logLevel >= logKeyLevel;
  }

  private getDefaultLevelForEnv(): LogLevel {
    switch (environment.RUNTIME_ENV) {
      case 'development':
        return 'debug';
      case 'production':
        return 'error';
      default:
        return 'info';
    }
  }

  private setupKeyStorage() {
    this.initKeyStorage();
    this.readKeyLevelFromStorage();
    this.readKeysFromStorage();
    window.addEventListener('storage', this.handleKeyStorageChange);
  }

  private initKeyStorage() {
    if (!localStorage.getItem(APP_LOGS_KEYS_KEY)) {
      localStorage.setItem(
        APP_LOGS_KEYS_KEY,
        JSON.stringify({ example: 'info' }),
      );
    }
    if (!localStorage.getItem(APP_LOGS_LEVEL_KEY)) {
      localStorage.setItem(APP_LOGS_LEVEL_KEY, '');
    }
  }

  private handleKeyStorageChange = (event: StorageEvent) => {
    if (event.key === APP_LOGS_KEYS_KEY) {
      this.readKeysFromStorage();
      this._logger.info('log levels updated', this._levels);
    } else if (event.key === APP_LOGS_LEVEL_KEY && event.newValue !== null) {
      this.readKeyLevelFromStorage();
      this._logger.info('log level updated', this._maxLogLevel);
    }
  };

  private readKeyLevelFromStorage() {
    const level = localStorage.getItem(APP_LOGS_LEVEL_KEY);
    if (level !== null && level !== '') {
      this._maxLogLevel = this.logLevelToNumber(level as LogLevel);
    } else {
      this._maxLogLevel = this.logLevelToNumber(this.getDefaultLevelForEnv());
    }
  }

  private readKeysFromStorage() {
    const keys = JSON.parse(localStorage.getItem(APP_LOGS_KEYS_KEY) ?? '{}');

    // update our levels
    for (const key in keys) {
      this._levels[key] = keys[key];
    }
  }
}

export default AppLogs;
